import type { ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { QUIZ } from "@/theme/quizTokens";
import { quizAsset } from "@/components/quiz/shared/quizAssets";
import { REVIEW_QUOTES, StarsRow } from "../review/ReviewSections";

/**
 * Components that exist only in the arm "b" of the plan test
 * (paws-on-plates#125) (src/experiments/planLadder.ts). Control never renders these.
 *
 * Copy personalises to the dog's name for single-dog households and falls
 * back to neutral wording for multi-dog households.
 */

/** "Milo's" for one dog, null for several (callers pick neutral copy). */
export function possessiveDogName(names: string[]): string | null {
    if (names.length !== 1) return null;
    return `${names[0]}'s`;
}

// --- Clean Bowl Promise copy (verbatim from paws-on-plates#125) ---------
const PROMISE_EYEBROW = "The Clean Bowl Promise";
const promiseHeadline = (dog: string | null) =>
    `If ${dog ?? "your dog"} doesn't love it, you don't pay for it.`;
const PROMISE_BODY =
    "Email us any time in the first 30 days and we'll refund your box in full. " +
    "Nothing to send back, nothing to fill in, and you keep whatever's left in the freezer.";
/** Real Trustpilot review (Laurence) about a fussy eater - also used in WhatYouSayCard. */
const FUSSY_REVIEW = REVIEW_QUOTES[0];

/** Filled green offer banner (replaces the blue centred offer text). */
export function PlanOfferBanner() {
    return (
        <Box
            sx={{
                bgcolor: QUIZ.color.green,
                borderRadius: "12px",
                py: { xs: "10px", md: "14px" },
                px: "16px",
                mb: { xs: "16px", md: "22px" },
                textAlign: "center",
            }}
        >
            <Typography
                sx={{
                    fontFamily: QUIZ.font.body,
                    fontWeight: 700,
                    fontSize: { xs: 15, md: 18 },
                    color: QUIZ.color.ink,
                }}
            >
                25% off your first two boxes
            </Typography>
        </Box>
    );
}

/** Green three-tick reassurance strip + freezer/flexibility line, inside the brown plan box. */
export function PlanReassurance({ dogNames }: { dogNames: string[] }) {
    const dogPossessive = possessiveDogName(dogNames);
    const dog = dogNames.length === 1 ? dogNames[0] : null;

    const items: ReactNode[] = [
        `Free delivery on ${dogPossessive ?? "your"} first box`,
        "Skip, change or cancel any time, by phone, email or online.",
        <>
            <strong>
                If {dog ?? "your dog"} won't eat it, we'll refund you in full.
            </strong>{" "}
            One email, and you keep the food.
        </>,
    ];

    return (
        <>
            <Box
                sx={{
                    bgcolor: QUIZ.color.green,
                    borderRadius: "16px",
                    p: { xs: "16px 18px", md: "22px 28px" },
                    mt: { xs: "4px", md: "8px" },
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: "10px", md: "12px" },
                }}
            >
                {items.map((item, i) => (
                    <Box key={i} sx={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                        <CheckIcon sx={{ fontSize: { xs: 20, md: 24 }, color: QUIZ.color.ink, mt: "1px", flexShrink: 0 }} />
                        <Typography
                            sx={{
                                fontFamily: QUIZ.font.body,
                                fontSize: { xs: 15, md: 17 },
                                lineHeight: 1.4,
                                color: QUIZ.color.ink,
                            }}
                        >
                            {item}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <Typography
                sx={{
                    fontFamily: QUIZ.font.body,
                    fontSize: { xs: 14, md: 16 },
                    lineHeight: 1.45,
                    color: QUIZ.color.cream2,
                    textAlign: "center",
                    mt: { xs: "16px", md: "20px" },
                    px: { xs: "4px", md: "24px" },
                }}
            >
                <strong>Tight on freezer space?</strong> You can move to smaller, more frequent
                boxes any time after your first one.
            </Typography>
        </>
    );
}

/** Inline brand-blue CTA under the plan box (the sticky footer stays as well). */
export function PlanInlineCta({ dogNames, onClick }: { dogNames: string[]; onClick: () => void }) {
    const dogPossessive = possessiveDogName(dogNames);
    return (
        <Box
            role="button"
            onClick={onClick}
            sx={{
                bgcolor: QUIZ.color.blue,
                color: QUIZ.color.ink,
                fontFamily: QUIZ.font.button,
                fontWeight: 700,
                fontSize: { xs: 18, md: 20 },
                textAlign: "center",
                borderRadius: QUIZ.radius.pill,
                py: { xs: "18px", md: "20px" },
                px: "24px",
                cursor: "pointer",
                userSelect: "none",
                maxWidth: { xs: "100%", sm: 520, md: 700 },
                mx: "auto",
                mt: { xs: "-10px", md: "-16px" },
                mb: { xs: "8px", md: "16px" },
                "&:hover": { opacity: 0.9 },
            }}
        >
            Choose {dogPossessive ?? "your"} meals
        </Box>
    );
}

/** Guarantee module below the CTA: photo + 30-day stamp, promise copy, fussy-eater review. */
export function CleanBowlPromise({ dogNames }: { dogNames: string[] }) {
    const dog = dogNames.length === 1 ? dogNames[0] : null;

    return (
        <Box
            sx={{
                maxWidth: { xs: "100%", sm: 520, md: 700 },
                mx: "auto",
                mt: { xs: "28px", md: "40px" },
                bgcolor: QUIZ.color.warmWhite,
                borderRadius: "20px",
                overflow: "hidden",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            }}
        >
            <Box sx={{ position: "relative" }}>
                <Box
                    component="img"
                    src={quizAsset("review-bowls-collie")}
                    alt=""
                    sx={{ width: "100%", height: { xs: 190, md: 260 }, objectFit: "cover", display: "block" }}
                />
                {/* 30-day stamp */}
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        right: { xs: 14, md: 24 },
                        bottom: { xs: -34, md: -44 },
                        width: { xs: 84, md: 108 },
                        height: { xs: 84, md: 108 },
                        borderRadius: "50%",
                        bgcolor: QUIZ.color.green,
                        border: `3px dashed ${QUIZ.color.ink}`,
                        outline: `4px solid ${QUIZ.color.green}`,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        transform: "rotate(-10deg)",
                        color: QUIZ.color.ink,
                        textAlign: "center",
                        lineHeight: 1,
                    }}
                >
                    <Typography sx={{ fontFamily: QUIZ.font.display, fontWeight: 900, fontSize: { xs: 28, md: 36 }, lineHeight: 1 }}>
                        30
                    </Typography>
                    {["day", "promise"].map((w) => (
                        <Typography
                            key={w}
                            sx={{ fontFamily: QUIZ.font.body, fontWeight: 700, fontSize: { xs: 9, md: 11 }, letterSpacing: 0.6, lineHeight: 1.15, textTransform: "uppercase" }}
                        >
                            {w}
                        </Typography>
                    ))}
                </Box>
            </Box>

            <Box sx={{ p: { xs: "28px 22px 26px", md: "40px 44px 36px" } }}>
                <Typography
                    sx={{
                        fontFamily: QUIZ.font.body,
                        fontWeight: 700,
                        fontSize: { xs: 12, md: 14 },
                        letterSpacing: 1,
                        textTransform: "uppercase",
                        color: QUIZ.color.brown,
                        mb: "8px",
                        pr: { xs: "80px", md: "110px" },
                    }}
                >
                    {PROMISE_EYEBROW}
                </Typography>
                <Typography
                    sx={{
                        fontFamily: QUIZ.font.label,
                        fontSize: { xs: 24, md: 30 },
                        lineHeight: 1.2,
                        color: QUIZ.color.ink,
                        mb: "12px",
                        pr: { xs: "80px", md: "110px" },
                    }}
                >
                    {promiseHeadline(dog)}
                </Typography>
                <Typography
                    sx={{
                        fontFamily: QUIZ.font.body,
                        fontSize: { xs: 15, md: 17 },
                        lineHeight: 1.5,
                        color: QUIZ.color.ink,
                        mb: { xs: "22px", md: "28px" },
                    }}
                >
                    {PROMISE_BODY}
                </Typography>

                <Box sx={{ borderTop: `1px solid ${QUIZ.color.cream2}`, pt: { xs: "20px", md: "24px" } }}>
                    <StarsRow size={20} />
                    <Typography
                        sx={{
                            fontFamily: QUIZ.font.body,
                            fontSize: { xs: 15, md: 16 },
                            lineHeight: 1.5,
                            color: QUIZ.color.grey,
                            mt: "12px",
                            mb: "10px",
                        }}
                    >
                        "{FUSSY_REVIEW.quote}"
                    </Typography>
                    <Typography sx={{ fontFamily: QUIZ.font.label, fontWeight: 700, fontSize: 16, color: QUIZ.color.brown }}>
                        {FUSSY_REVIEW.author}, Trustpilot
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
